/**
 * Payment-related API service functions
 */
import apiClient from './apiClient';

export const paymentService = {
  /**
   * Create Stripe checkout session
   * @param {Object} bookingData - Booking information
   * @param {number} amount - Total amount
   * @param {string} bookingToken - Booking token (for rebooking)
   * @param {boolean} isRebooking - Whether this is a flight change/rebooking
   * @returns {Promise} Stripe session details
   */
  createStripeSession: async (bookingData, amount, bookingToken = null, isRebooking = false) => {
    return apiClient.post('/payments/stripe/create-session', {
      booking_data: bookingData,
      amount: amount,
      booking_token: bookingToken,
      is_rebooking: isRebooking
    });
  },

  /**
   * Create PayPal payment
   * @param {Object} bookingData - Booking information
   * @param {number} amount - Total amount
   * @param {string} bookingToken - Booking token (for rebooking)
   * @param {boolean} isRebooking - Whether this is a flight change/rebooking
   * @returns {Promise} PayPal payment details
   */
  createPayPalPayment: async (bookingData, amount, bookingToken = null, isRebooking = false) => {
    return apiClient.post('/payments/paypal/create-payment', {
      booking_data: bookingData,
      amount: amount,
      booking_token: bookingToken,
      is_rebooking: isRebooking
    });
  },

  /**
   * Validate promotion code (Stripe checkout catalog).
   * @param {string} code - Promotion code e.g. FLIGHT20
   * @param {number} amount - Subtotal before discount
   */
  validatePromotionCode: async (code, amount) => {
    return apiClient.post('/payments/promo/validate', {
      code,
      amount
    });
  },

  /**
   * List active promotion codes from Stripe (for Stripe Checkout).
   * @param {number} [amount] - Optional order subtotal to mark eligibility
   */
  listPromotionCodes: async (amount) => {
    const params =
      amount != null && !Number.isNaN(Number(amount)) && Number(amount) > 0
        ? { amount: Number(amount) }
        : {};
    return apiClient.get('/payments/promo/list', params);
  },

  /**
   * Get payment history
   * @returns {Promise} List of payments
   */
  getPaymentHistory: async () => {
    return apiClient.get('/payments/history');
  },

  /**
   * Verify Stripe payment session
   * @param {string} sessionId - Stripe session ID
   * @returns {Promise} Payment verification result
   */
  verifyStripePayment: async (sessionId) => {
    return apiClient.get(`/payments/stripe/verify/${sessionId}`);
  },

  /**
   * Confirm free rebooking (when no payment is required)
   * @param {string} bookingToken - Booking token
   * @returns {Promise} Confirmation result
   */
  confirmFreeRebooking: async (bookingToken) => {
    return apiClient.post('/payments/rebooking/confirm-free', {
      booking_token: bookingToken
    });
  },

  /**
   * Verify routes are not cancelled before payment.
   * @param {{ route_ids?: number[], booking_data?: object }} payload
   */
  verifyFlightsForPayment: async (payload) => {
    return apiClient.post('/payments/verify-flights', payload);
  }
};

export default paymentService;
